import { useEffect, useState } from "react";
import { FadeIn, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEADERS = [
  {
    name: "Suneer S",
    role: "Managing Director",
    quote:
      "Thirty years of trust stands on our people. Whoever joins Techworld gets real responsibility, a leadership team that listens, and a career that grows as the group grows. Your opportunity here is limited only by your own ambition.",
  },
  {
    name: "Jubiya Mathew",
    role: "Branch Manager — Ambalappuzha",
    quote: "I've grown with Techworld year after year — the training, the trust and the team spirit here are unlike anywhere I've worked.",
  },
  {
    name: "Nisha Devaraj",
    role: "Branch Manager — Alappuzha",
    quote: "From day one I was given real responsibility. Every process is documented, so you always know exactly what is expected of you.",
  },
  {
    name: "Mohammed Rizwan",
    role: "Branch Manager — Kottayam",
    quote: "The best part of working here is direct access to leadership — your ideas are heard and acted on, not lost in layers.",
  },
  {
    name: "Ajeesh U",
    role: "Branch Manager — Cherthala",
    quote: "Techworld invests in its people — I've watched colleagues build long, stable careers here with clear growth at every step.",
  },
  {
    name: "Anju Sreedhar",
    role: "Branch Manager — Aroor",
    quote: "Aroor feels like one family — from the newest trainee to the manager, everyone works as a team, and that is what makes every day here worthwhile.",
  },
];

const initials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export const Leadership = () => {
  const [managers, setManagers] = useState({});
  const [photos, setPhotos] = useState({});
  const [mdPhoto, setMdPhoto] = useState(null);

  useEffect(() => {
    fetch(`${API}/branches`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((docs) => {
        const m = {};
        const p = {};
        (docs || []).forEach((d) => {
          if (d.manager) m[d.name] = d.manager;
          if (d.photo) p[d.name] = d.photo;
        });
        setManagers(m);
        setPhotos(p);
      })
      .catch(() => {});
    fetch(`${API}/leader-photos`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setMdPhoto(d?.md || null))
      .catch(() => {});
  }, []);

  const displayLeaders = LEADERS.map((m) => {
    const branchName = m.role.startsWith("Branch Manager — ") ? m.role.split("— ")[1]?.trim() : null;
    const live = branchName && managers[branchName];
    const photo = m.role === "Managing Director" ? mdPhoto : branchName ? photos[branchName] : null;
    return { ...m, name: live || m.name, photo: photo || null };
  });

  return (
    <section id="leadership" className="relative py-20 sm:py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        kicker="05 — Leadership"
        title="The people behind the name."
        description="Names and designations — hear it from the team itself."
      />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {displayLeaders.map((m, i) => (
          <FadeIn key={m.name} delay={i * 0.06} className={i === 0 ? "sm:col-span-2 lg:col-span-3" : "h-full"}>
            <div
              data-testid="leader-card"
              className={`flex h-full items-center gap-5 rounded-2xl border p-6 transition-all duration-300 sm:p-8 ${
                i === 0
                  ? "border-brand-red/50 bg-brand-red/[0.08] shadow-[0_0_40px_rgba(220,38,38,0.12)]"
                  : "border-white/10 bg-white/[0.04] hover:border-brand-red/40"
              }`}
            >
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-brand-red/40 bg-brand-red/10 font-display text-xl font-semibold text-brand-bright sm:h-20 sm:w-20 sm:text-2xl">
                {m.photo ? (
                  <img src={m.photo} alt={m.name} className="h-full w-full object-cover" data-testid="leader-photo" />
                ) : (
                  initials(m.name)
                )}
              </div>
              <div className="min-w-0">
                <h4 className="font-display text-xl font-semibold text-white sm:text-2xl">{m.name}</h4>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-bright sm:text-[11px]">
                  {m.role}
                </p>
                <p
                  data-testid="leader-quote"
                  className="mt-3 border-l-2 border-brand-red/60 pl-3 text-sm italic leading-relaxed text-slate-300"
                >
                  "{m.quote}"
                </p>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
  );
};

export default Leadership;
